import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState({
    street: '',
    city: '',
    province: '',
    postalCode: '',
  });
  const [team, setTeam] = useState(''); // position
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Prefill fields from invite link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inviteName = params.get('name');
    const invitePosition = params.get('position');
    if (inviteName) setName(inviteName);
    if (invitePosition) setTeam(invitePosition);
  }, []);

  // Validation for registration
  const registrationValid =
    !!name &&
    !!phone &&
    !!address.street &&
    !!address.city &&
    !!address.province &&
    !!address.postalCode &&
    !!email &&
    !!password;

  // (placeholder removed)

  // Handle registration and login (Firebase Auth + Firestore user profile)
  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegistering) {
        if (!registrationValid) {
          setError('Please fill out all required fields.');
          setLoading(false);
          return;
        }

        const cred = await createUserWithEmailAndPassword(auth, email, password);

        // Store user profile in Firestore (role/active are admin-managed via console)
        await setDoc(doc(db, "users", cred.user.uid), {
          email,
          name,
          phone,
          address,
          team: team || "Rope Access Technician",
          createdAt: serverTimestamp(),
        });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }

      // Exchange Firebase ID token -> backend JWT (required for /templates, /inspections, etc.)
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';
      const fbUser = auth.currentUser;
      const idToken = fbUser ? await fbUser.getIdToken() : null;

      if (!idToken) {
        throw new Error("Firebase login succeeded, but no ID token was available.");
      }

      const resp = await fetch(`${baseUrl}/auth/firebase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error || "Backend login failed");
      }

      // IMPORTANT: this is what your api.js reads and sends to the backend
      localStorage.setItem("token", data.token);

      setLoading(false);
      if (typeof onLogin === "function") onLogin('dashboard');
      navigate("/dashboard");
    } catch (err) {
      setError(err?.message || "Authentication failed");
      setLoading(false);
    }
  };

  // Microsoft login via backend (Azure AD)
  const handleMicrosoftLogin = () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';
    window.location.href = `${baseUrl}/auth/ms/login`;
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
      <div className="w-full max-w-sm bg-white p-6 shadow-md rounded-md max-h-[95vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-center mb-4">
          {isRegistering ? 'Create Your Account' : 'Login'}
        </h2>

        <form onSubmit={handleAuth}>
          <input
            type="email"
            placeholder="Email"
            className="w-full p-2 mb-3 border rounded"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoFocus
            required
            disabled={isRegistering && !!window.location.search} // lock if from invite
          />

          {isRegistering && (
            <>
              <input
                type="text"
                placeholder="Full Name"
                className="w-full p-2 mb-3 border rounded"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full p-2 mb-3 border rounded"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="Street"
                className="w-full p-2 mb-3 border rounded"
                value={address.street}
                onChange={e => setAddress({ ...address, street: e.target.value })}
                required
              />
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="City"
                  className="w-full p-2 mb-3 border rounded"
                  value={address.city}
                  onChange={e => setAddress({ ...address, city: e.target.value })}
                  required
                />
                <input
                  type="text"
                  placeholder="Province"
                  className="w-full p-2 mb-3 border rounded"
                  value={address.province}
                  onChange={e => setAddress({ ...address, province: e.target.value })}
                  required
                />
              </div>
              <input
                type="text"
                placeholder="Postal Code"
                className="w-full p-2 mb-3 border rounded"
                value={address.postalCode}
                onChange={e => setAddress({ ...address, postalCode: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Position"
                className="w-full p-2 mb-3 border rounded bg-gray-100"
                value={team}
                disabled
              />
            </>
          )}

          <input
            type="password"
            placeholder="Password"
            autoComplete="current-password"
            className="w-full p-2 mb-3 border rounded"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />

          {error && (
            <div className="text-red-500 text-sm mb-3">{error}</div>
          )}

          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded mb-2"
            disabled={loading || (isRegistering && !registrationValid)}
          >
            {loading
              ? 'Please wait...'
              : isRegistering
              ? 'Register'
              : 'Login'}
          </button>
        </form>

        {/* Microsoft login */}
        <button
          type="button"
          onClick={handleMicrosoftLogin}
          className="w-full border py-2 rounded mb-3"
          disabled={loading}
        >
          Sign in with Microsoft
        </button>

        <div className="text-center mt-4">
          <button
            onClick={() => setIsRegistering(!isRegistering)}
            className="text-sm text-blue-600 hover:underline"
            disabled={loading}
          >
            {isRegistering
              ? 'Already have an account? Login'
              : 'No account? Register'}
          </button>
        </div>
      </div>
    </div>
  );
}
